import {
	CreatedUserAttendanceDto,
	IndividualAttendanceDto,
} from "@/utils/validators/attendances.validator";
import { useCallback, useEffect, useRef, useState } from "react";

// hooks
import useAuth from "@/hooks/useAuth";
import { endOfDay, format, startOfDay } from "date-fns";

// components
import { opusClient } from "@/utils/rest-client/opus-client";
import { useTsRestQueryClient } from "@ts-rest/react-query";
import toast from "react-hot-toast";
import Webcam from "react-webcam";
import { confirmDialog } from "../../common/Dialog/Dialog";
import LoaderSpinner from "../../common/LoadSpinner/LoadSpinner";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils";
// import { useTimeLocation } from "@/store/useTimeLocation";
import { getWeekDates } from "@/utils/constants/helpers";
import { UserActivity } from "@/utils/validators/user-status.validator";
import Modal from "@/components/common/Modal/Modal";
import { Button } from "@/components/ui/button";
import useCameraCheck from "@/hooks/useCamera";
import PrivacyPolicy from "../PrivacyPolicy/PrivacyPolicy";

export interface ICamera {
	latestTimeLog?: CreatedUserAttendanceDto;
	isLatestTimeLogToday?: boolean;
	refetchLatestTimeLog: () => void;
	setOpen: (x: boolean) => void;
	purpose: "Clock In" | "Clock Out";
}
interface ILocation {
	lat: number;
	lng: number;
}

const videoConstraints = {
	width: 540,
	// facingMode: "environment",
	facingMode: "user",
};

const Camera = ({
	setOpen,
	latestTimeLog,
	isLatestTimeLogToday,
	refetchLatestTimeLog,
	purpose,
}: ICamera) => {
	const { user, getMe, getUsers } = useAuth();
	const { location, isLoading } = useAuth();
	const apiQueryClient = useTsRestQueryClient(opusClient);
	const { start, end } = getWeekDates();
	// const { data } = useTimeLocation();
	const [startDate] = useState<Date>(new Date(start));
	const [endDate] = useState<Date>(new Date(end));
	const [isModalLocation, setIsModalLocation] = useState<boolean>(false);
	const [isCameraModal, setIsCameraModal] = useState<boolean>(false);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [showPopover, setShowPopover] = useState<boolean>(false);

	const { data: dataCamera, isLoading: cameraLoading } = useCameraCheck();

	const { mutate: mutateUserStatus } =
		apiQueryClient.users.updateUserStatus.useMutation();

	const { mutateAsync: mutateTimeLog } =
		apiQueryClient.users.upsertUserAttendance.useMutation({
			onSuccess: async () => {
				toast.success("Attendance is logged!");

				apiQueryClient.users.getUserGroupedAttendances.fetchQuery(
					["user-attendances", startDate],
					{
						params: { userId: user!.id },
						query: {
							startDate: startDate.toISOString(),
							endDate: endDate.toISOString(),
						},
					}
				);
				apiQueryClient.attendance.getAttendances.fetchQuery(
					["Attendances", startOfDay(new Date()).toISOString()],
					{
						query: {
							startDate: startOfDay(new Date()).toISOString(),
							endDate: endOfDay(new Date()).toISOString(),
						},
					}
				);
				mutateUserStatus({
					params: { userId: user!.id },
					body: {
						status: purpose === "Clock In" ? "clocked-in" : "clocked-out",
						activity: UserActivity.Online,
					},
				});
				await getMe();
				await getUsers();
			},
		});

	const { mutateAsync: uploadFile } =
		apiQueryClient.files.uploadFile.useMutation();

	const webCamRef = useRef<Webcam | null>(null);
	const [isCameraLoading, setCameraLoading] = useState(true);

	const [currentTime, setCurrentTime] = useState<string>(
		format(new Date(), "hh:mm a")
	);
	const [userPosition, setUserPosition] = useState<ILocation>({
		lat: Number(location.lat),
		lng: Number(location.lng),
	});

	const capturePhoto = useCallback(async () => {
		if (!isChecked) {
			setShowPopover(true);
			return false;
		}
		try {
			const imageSrc = webCamRef?.current?.getScreenshot();

			if (navigator.geolocation) {
				navigator.permissions
					.query({ name: "geolocation" })
					.then(function (result) {
						if (result.state === "denied") {
							setIsModalLocation(true);
							return false;
						}
					});
			}

			if (!dataCamera) {
				setIsCameraModal(true);
				return false;
			}

			if (!imageSrc) {
				throw new Error("Failed to capture image from webcam.");
			}

			const response = await fetch(imageSrc);

			if (!response.ok) {
				throw new Error(`Failed to fetch image from ${imageSrc}`);
			}

			const blob = await response.blob();
			const formData = new FormData();
			formData.append("file", blob);
			formData.append("permission", '"account"');

			const uploadResponse = await uploadFile({
				body: formData,
				extraHeaders: { "Content-Type": "multipart/form-data" },
			});

			const currDate = new Date();
			const isoDate = currDate.toISOString();

			const individualTimeLog: IndividualAttendanceDto = {
				lat: Number(location.lat),
				long: Number(location.lng),
				datetime: isoDate,
				pictureId: uploadResponse.body.id,
			};

			switch (purpose) {
				case "Clock In":
					await mutateTimeLog({
						body: { userId: user!.id, in: individualTimeLog },
						params: { userId: user!.id },
					});

					break;
				case "Clock Out":
					if (isLatestTimeLogToday) {
						return confirmDialog({
							title: "Update time log",
							message: "Are you sure you want to update time log?",
							confirmText: "Update",
							onSubmit: async () => {
								await setOpen(false);
								await mutateTimeLog({
									body: {
										userId: user!.id,
										id: latestTimeLog?.id,
										out: individualTimeLog,
									},
									params: { userId: user!.id },
								});
							},
						});
					}
					await mutateTimeLog({
						body: {
							userId: user!.id,
							id: latestTimeLog?.id,
							out: individualTimeLog,
						},
						params: { userId: user!.id },
					});
			}
			await setOpen(false);
			await refetchLatestTimeLog();
		} catch (err) {
			toast.error("Error capturing photo and logging time");
		}
	}, [webCamRef, latestTimeLog, userPosition, dataCamera, isChecked]);

	useEffect(() => {
		if (location.lat && location.lng) {
			setUserPosition({ lat: Number(location.lat), lng: Number(location.lng) });
		}
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTime(format(new Date(), "hh:mm a"));
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	// useEffect(() => {
	// 	return () => {
	// 		if (webCamRef.current) {
	// 			const stream = webCamRef.current.stream;
	// 			console.log(stream);
	// 			if (stream) {
	// 				const tracks = stream.getTracks();

	// 				tracks.forEach((track) => track.stop());
	// 			}
	// 		}
	// 	};
	// }, []);

	if (isLoading)
		return (
			<div className="flex justify-center items-center h-full w-full">
				<LoaderSpinner className="w-10 h-20" />
			</div>
		);

	return (
		<div>
			<div className="grid rounded-[12px] overflow-hidden transition-all">
				<Skeleton
					className={cn([
						"col-start-1 row-start-1 w-full min-h-full h-64  bg-opus-gray-300 transition-all",
						!isCameraLoading && !cameraLoading && "opacity-0 animate-none",
					])}></Skeleton>
				<Webcam
					className="col-start-1 row-start-1"
					ref={webCamRef}
					audio={false}
					screenshotFormat="image/jpeg"
					videoConstraints={videoConstraints}
					onUserMedia={() => setCameraLoading(false)}
				/>
			</div>

			<div className="text-center pt-5 text-sm md:text-base">
				{currentTime} - {location.current_location}, {location.country}
			</div>
			{/* <div className="text-center pt-5">{data}</div> */}

			<div className="md:mt-5 md:mb-5 ">
				<div className="flex items-center justify-center">
					<PrivacyPolicy
						isChecked={isChecked}
						setIsChecked={setIsChecked}
						showPopover={showPopover}
					/>
				</div>
			</div>

			<div className="flex items-center justify-center ">
				<Button
					variant="default"
					className="!p-4 !w-44 block font-gilroy-semibold rounded-full bg-opus-blue-100 flex items-center"
					onClick={() => capturePhoto()}>
					Take a snap to {purpose.toLocaleLowerCase()}
				</Button>
			</div>

			<Modal
				title="Allow Location"
				isOpen={isModalLocation}
				setOpen={setIsModalLocation}>
				<div className="mb-5">
					Location access was denied. Please enable it in your browser settings.
				</div>
				<div className="flex items-end justify-end">
					<Button
						variant="default"
						className="!p-2 !w-44 block font-gilroy-semibold rounded-full bg-opus-blue-100 flex items-center"
						onClick={() => setIsModalLocation(false)}>
						Close
					</Button>
				</div>
			</Modal>

			<Modal
				title="Allow Camera"
				isOpen={isCameraModal}
				setOpen={setIsCameraModal}>
				<div className="mb-5">
					Camer access was denied. Please enable it in your browser settings.
				</div>
				<div className="flex items-end justify-end">
					<Button
						variant="default"
						className="!p- !w-44 block font-gilroy-semibold rounded-full bg-opus-blue-100 flex items-center"
						onClick={() => setIsCameraModal(false)}>
						Close
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default Camera;
